import type { ContactSalesFormValues } from "./types";

export const CONTACT_SALES_STORAGE_KEY = "contact_sales_form_v2";

/**
 * CSF_VERSION_IDS — permanent ledger of every contact sales form variant ever shipped.
 *
 * RULES:
 *  1. Never remove or rename an existing entry — IDs are written to the CRM on submission
 *     and must remain interpretable indefinitely.
 *  2. Each A/B experiment adds its own control + test pair below the previous one.
 *  3. The `baseline` value is used when no experiment is active (default render).
 *  4. Naming convention: csf_{experiment_descriptor}_{variant}
 *
 * To add a new experiment, append a new pair and wire up the new csfVersionId at the
 * ContactSales call site — do not modify existing entries.
 */
export const CSF_VERSION_IDS = {
  baseline: "csf_baseline",

  // Experiment: form field redesign (Q2 2026)
  form_fields_control: "csf_form_fields_control",
  form_fields_test: "csf_form_fields_test",
} as const;

export type CsfVersionId = (typeof CSF_VERSION_IDS)[keyof typeof CSF_VERSION_IDS];

/**
 * Returns true when the given version ID corresponds to an A/B test variant
 * (as opposed to a control or baseline). Add new test variant IDs here when
 * creating new experiments — explicit opt-in is safer than negative exclusion.
 */
export function isTestVariantId(id: CsfVersionId): boolean {
  const testVariantIds: ReadonlyArray<CsfVersionId> = [CSF_VERSION_IDS.form_fields_test];
  return testVariantIds.includes(id);
}

/** Progress bar segments on the A/B variant (form steps only, excludes thank-you screen). */
export const CONTACT_SALES_VARIANT_PROGRESS_STEPS = 2;

export type AgentRoutingRule = {
  matchRegions: string[];
  matchIsPersonal?: boolean; // undefined means "any"
  agentId: string;
  excludeCompanyName?: boolean;
};

export const AGENT_ROUTING_RULES: AgentRoutingRule[] = [
  {
    matchRegions: ["Brazil"],
    matchIsPersonal: true,
    agentId: "agent_7601k2pjz192e8m91a54mcmzvbzk",
  },
  {
    matchRegions: ["India"],
    agentId: "agent_5101kaznbwb8fspt8f39skaqksjx",
    excludeCompanyName: true,
  },
  {
    matchRegions: ["Europe"],
    agentId: "agent_9101k6aqd8ctewj953p8xc6y93sb",
  },
  {
    matchRegions: ["South Asia", "East Asia & Pacific", "Oceania"],
    matchIsPersonal: true,
    agentId: "agent_9101kc956edyfv5v9rd24dqnt9cr",
    excludeCompanyName: true,
  },
  {
    matchRegions: ["Spanish LATAM"],
    agentId: "agent_5101kchb5j0jfm1bny5sx10bd10g",
  },
];

export const CONTACT_SALES_FORM_DEFAULTS: ContactSalesFormValues = {
  email: "",
  website: "",
  first_name: "",
  last_name: "",
  company: "",
  company_size: "",
  industry: "",
  expected_monthly_usage: "",
  country: "United States of America",
  phone_number: "",
  detail: "",
  how_did_you_hear: "",
  how_did_you_hear_other: "",
  platform_interest: "",
};

export const fieldConfig = {
  website: {
    name: "website",
    required: true,
  },
  email: {
    name: "email",
    type: "email",
    required: true,
  },
  first_name: {
    name: "first_name",
    required: true,
  },
  last_name: {
    name: "last_name",
    required: true,
  },
  company: {
    name: "company",
    required: true,
  },
  industry: {
    name: "industry",
    required: true,
  },
  company_size: {
    name: "company_size",
    required: true,
  },
  country: {
    name: "country",
    required: true,
  },
  phone_number: {
    name: "phone_number",
    required: true,
  },
  detail: {
    name: "detail",
    required: true,
  },
  how_did_you_hear: {
    name: "how_did_you_hear",
    required: false,
  },
  how_did_you_hear_other: {
    name: "how_did_you_hear_other",
    required: false,
  },
  platform_interest: {
    name: "platform_interest",
    required: false,
  },
  expected_monthly_usage: {
    name: "expected_monthly_usage",
    required: true,
  },
} as const;

export const personalDomains = [
  "@gmail.com",
  "@yahoo.com",
  "@hotmail.com",
  "@outlook.com",
  "@aol.com",
  "@icloud.com",
  "@proton.me",
  "@protonmail.com",
  "@mail.com",
] as const;

export const fieldOptionKeys = {
  industry: [
    { translationKey: "fieldOptions.industry.accounting", value: "Accounting" },
    { translationKey: "fieldOptions.industry.advertising", value: "Advertising" },
    { translationKey: "fieldOptions.industry.animation", value: "Animation" },
    { translationKey: "fieldOptions.industry.automotive", value: "Automotive" },
    { translationKey: "fieldOptions.industry.banking", value: "Banking" },
    { translationKey: "fieldOptions.industry.bpos", value: "BPOs" },
    { translationKey: "fieldOptions.industry.call_centers", value: "Call Centers" },
    {
      translationKey: "fieldOptions.industry.conversational_consumer_applications",
      value: "Conversational Consumer Applications",
    },
    { translationKey: "fieldOptions.industry.digital_publishing", value: "Digital Publishing" },
    { translationKey: "fieldOptions.industry.e_commerce", value: "E-Commerce" },
    { translationKey: "fieldOptions.industry.education_technology", value: "Education Technology" },
    { translationKey: "fieldOptions.industry.financial_services", value: "Financial Services" },
    { translationKey: "fieldOptions.industry.food", value: "Food" },
    { translationKey: "fieldOptions.industry.gaming", value: "Gaming" },
    { translationKey: "fieldOptions.industry.government", value: "Government" },
    { translationKey: "fieldOptions.industry.hardware", value: "Hardware" },
    { translationKey: "fieldOptions.industry.health_and_wellness", value: "Health and Wellness" },
    { translationKey: "fieldOptions.industry.hospitality_travel", value: "Hospitality & Travel" },
    { translationKey: "fieldOptions.industry.human_resources", value: "Human Resources" },
    { translationKey: "fieldOptions.industry.insurance", value: "Insurance" },
    { translationKey: "fieldOptions.industry.internet_technology_services", value: "Internet & Technology Services" },
    { translationKey: "fieldOptions.industry.legal_services", value: "Legal Services" },
    { translationKey: "fieldOptions.industry.logistics_transportation", value: "Logistics & Transportation" },
    { translationKey: "fieldOptions.industry.management_consulting", value: "Management Consulting" },
    { translationKey: "fieldOptions.industry.media_creation_tools", value: "Media Creation Tools" },
    { translationKey: "fieldOptions.industry.movies_streaming_services", value: "Movies & Streaming Services" },
    { translationKey: "fieldOptions.industry.music_production", value: "Music Production" },
    { translationKey: "fieldOptions.industry.news_publishers", value: "News Publishers" },
    { translationKey: "fieldOptions.industry.non_profit_organizations", value: "Non-profit Organizations" },
    {
      translationKey: "fieldOptions.industry.professional_training_coaching",
      value: "Professional Training & Coaching",
    },
    { translationKey: "fieldOptions.industry.public_education", value: "Public Education" },
    { translationKey: "fieldOptions.industry.retail", value: "Retail" },
    { translationKey: "fieldOptions.industry.sports", value: "Sports" },
    { translationKey: "fieldOptions.industry.staffing_recruiting", value: "Staffing & Recruiting" },
    { translationKey: "fieldOptions.industry.telecommunications", value: "Telecommunications" },
    { translationKey: "fieldOptions.industry.tv_radio_broadcasting", value: "TV & Radio Broadcasting" },
    { translationKey: "fieldOptions.industry.other", value: "Other" },
  ],
  platform_interest: [
    { translationKey: "fieldOptions.platform_interest.eleven_agents", value: "ElevenAgents" },
    { translationKey: "fieldOptions.platform_interest.eleven_creative", value: "ElevenCreative" },
    { translationKey: "fieldOptions.platform_interest.eleven_api", value: "ElevenAPI" },
  ],
  expected_monthly_usage: [
    { translationKey: "fieldOptions.expected_monthly_usage.over_100k_minutes", value: "Over 100,000 minutes" },
    { translationKey: "fieldOptions.expected_monthly_usage.over_10k_minutes", value: "Over 10,000 minutes" },
    { translationKey: "fieldOptions.expected_monthly_usage.under_10k_minutes", value: "Under 10,000 minutes" },
    { translationKey: "fieldOptions.expected_monthly_usage.not_relevant", value: "Not relevant" },
  ],
  how_did_you_hear: [
    { translationKey: "fieldOptions.how_did_you_hear.google_web_search", value: "Google or web search" },
    { translationKey: "fieldOptions.how_did_you_hear.youtube", value: "YouTube" },
    { translationKey: "fieldOptions.how_did_you_hear.chatgpt", value: "ChatGPT" },
    { translationKey: "fieldOptions.how_did_you_hear.tiktok", value: "TikTok" },
    { translationKey: "fieldOptions.how_did_you_hear.podcast", value: "Podcast" },
    {
      translationKey: "fieldOptions.how_did_you_hear.colleague_company",
      value: "A colleague or company I work with uses it",
    },
    { translationKey: "fieldOptions.how_did_you_hear.instagram", value: "Instagram" },
    { translationKey: "fieldOptions.how_did_you_hear.friend_referral", value: "Friend or personal referral" },
    {
      translationKey: "fieldOptions.how_did_you_hear.another_ai_assistant",
      value: "Another AI assistant (Claude, Gemini, Perplexity, etc.)",
    },
    {
      translationKey: "fieldOptions.how_did_you_hear.other_social_media",
      value: "Other social media (X, Facebook, LinkedIn, Reddit)",
    },
    { translationKey: "fieldOptions.how_did_you_hear.article_blog_press", value: "Article, blog, or press coverage" },
    {
      translationKey: "fieldOptions.how_did_you_hear.conference_event_webinar",
      value: "Conference, event, or webinar",
    },
    { translationKey: "fieldOptions.how_did_you_hear.online_ad", value: "Online ad" },
    { translationKey: "fieldOptions.how_did_you_hear.other", value: "Other" },
  ],
} as const;

export const companySizeOptions = [
  { label: "< 200", value: "< 200" },
  { label: "200 - 2000", value: "200 - 2000" },
  { label: "> 2000", value: "> 2000" },
] as const;
